import { Formik } from 'formik';

import { Messages } from 'config';
import { Flex } from 'components/flex';
import { Icon } from 'components/icon';
import { Dialog } from 'components/dialog';
import { ButtonColors, CloverVoidReason } from 'types';
import { useVoidPaymentMutation } from 'store/api/clover';
import { InputField, SelectField } from 'components/fields';
import { Button, ButtonTypes, ButtonWithRef } from 'components/button';
import {
  isMobile,
  useAlert,
  handleMutation,
  useSelectedCloverDevice,
} from 'hooks';

import * as CommonStyles from '../styles';

import * as Styles from './styles';
import { VoidCheckoutDialogProps } from './types';
import {
  getInitialValues,
  validationSchema,
  voidReasonOptions,
} from './config';

export const VoidCheckoutDialog = ({ checkout }: VoidCheckoutDialogProps) => {
  const mobile = isMobile();
  const { showSuccessAlert } = useAlert();

  const [createVoid, mutation] = useVoidPaymentMutation();
  handleMutation(mutation);

  const { deviceOptions, selectedDeviceId: deviceId } = useSelectedCloverDevice(
    checkout?.event
  );

  return (
    <Dialog
      showCloseButton
      title="Void Payments"
      closeOnDocumentClick={false}
      width={mobile ? '90wv' : '504px'}
      trigger={
        <ButtonWithRef
          text="Void"
          icon={<Icon.Coins />}
          colorType={ButtonColors.LightRed}
        />
      }
    >
      {(close) => (
        <Formik
          validationSchema={validationSchema}
          initialValues={getInitialValues(deviceId)}
          onSubmit={async (values) => {
            try {
              const response = await createVoid({
                id: checkout.id,
                eventId: checkout?.event?.id,
                deviceId: Number(values.deviceId),
                approvalPassword: values.approvalPassword,
                voidReason: values.voidReason as CloverVoidReason,
              }).unwrap();

              if (response?.id) {
                close();
                showSuccessAlert(
                  `Void Payment ${Messages.CREATED_SUCCESSFULLY}`
                );
              }
            } catch (error) {
              console.error(error);
            }
          }}
        >
          {({ dirty, isSubmitting }) => (
            <CommonStyles.Form autoComplete="off">
              {/* Hidden field for Safari cheating */}
              <Styles.HiddenPasswordInput
                type="password"
                autoComplete="new-password"
              />

              <Flex column gap={16}>
                <p>
                  After creating void, all funds will be returned from checkout,
                  and the product will be returned to the event. This action
                  cannot be undone.
                </p>

                <SelectField
                  onlyValue
                  name="deviceId"
                  label="Select Device"
                  options={deviceOptions}
                  placeholder="Select Device"
                />

                <SelectField
                  onlyValue
                  name="voidReason"
                  label="Void Reason"
                  placeholder="Select reason"
                  options={voidReasonOptions}
                />

                <InputField
                  number
                  autofill="off"
                  label="Manager PIN"
                  name="approvalPassword"
                  placeholder="Manager PIN"
                  autoComplete="one-time-code"
                />
              </Flex>

              <Styles.ButtonsContainer>
                <Button
                  text="Close"
                  onClick={close}
                  colorType={ButtonColors.LightRed}
                />
                <Button
                  text="Void"
                  type={ButtonTypes.Submit}
                  disabled={!dirty || isSubmitting}
                />
              </Styles.ButtonsContainer>
            </CommonStyles.Form>
          )}
        </Formik>
      )}
    </Dialog>
  );
};
